import {
  GlobalWorkerOptions,
  getDocument,
} from "pdfjs-dist";
import workerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import {
  detectDexaPageRole,
  getDexaVisualCropSpecs,
} from "./dexaVisuals.js";

GlobalWorkerOptions.workerSrc = workerSrc;

function groupTextItems(items) {
  const fragments = items
    .filter((item) => item.str?.trim())
    .map((item) => ({
      text: item.str.trim(),
      x: Number(item.transform?.[4]) || 0,
      y: Number(item.transform?.[5]) || 0,
      width: Number(item.width) || 0,
    }))
    .sort((left, right) => {
      if (Math.abs(right.y - left.y) > 2.5) return right.y - left.y;
      return left.x - right.x;
    });
  const lines = [];
  for (const fragment of fragments) {
    let line = lines.find((candidate) => Math.abs(candidate.y - fragment.y) <= 2.5);
    if (!line) {
      line = { y: fragment.y, fragments: [] };
      lines.push(line);
    }
    line.fragments.push(fragment);
  }
  return lines
    .sort((left, right) => right.y - left.y)
    .map((line) => line.fragments
      .sort((left, right) => left.x - right.x)
      .reduce((text, fragment, index, array) => {
        if (!index) return fragment.text;
        const previous = array[index - 1];
        const gap = fragment.x - (previous.x + previous.width);
        return `${text}${gap > 1.2 ? " " : ""}${fragment.text}`;
      }, "")
      .replace(/\s+/g, " ")
      .trim())
    .filter(Boolean);
}

function cropCanvas(canvas, crop) {
  const sourceX = Math.round(canvas.width * crop.x);
  const sourceY = Math.round(canvas.height * crop.y);
  const sourceWidth = Math.max(1, Math.round(canvas.width * crop.width));
  const sourceHeight = Math.max(1, Math.round(canvas.height * crop.height));
  const output = document.createElement("canvas");
  output.width = sourceWidth;
  output.height = sourceHeight;
  const context = output.getContext("2d", { alpha: false });
  context.drawImage(
    canvas,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    sourceWidth,
    sourceHeight,
  );
  const dataUrl = output.toDataURL("image/jpeg", 0.92);
  output.width = 0;
  output.height = 0;
  return dataUrl;
}

async function renderPagePreview(page, text) {
  const baseViewport = page.getViewport({ scale: 1 });
  const targetWidth = 1500;
  const scale = Math.min(2.25, targetWidth / baseViewport.width);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d", { alpha: false });
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  await page.render({
    canvasContext: context,
    viewport,
    background: "#ffffff",
  }).promise;
  const role = detectDexaPageRole(text);
  const visuals = Object.fromEntries(
    Object.entries(getDexaVisualCropSpecs(role))
      .map(([key, crop]) => [key, cropCanvas(canvas, crop)]),
  );
  const imageUrl = canvas.toDataURL("image/jpeg", 0.86);
  canvas.width = 0;
  canvas.height = 0;
  return { imageUrl, role, visuals };
}

export async function extractDexaPdf(file, { onProgress } = {}) {
  if (!file) throw new Error("Selecciona un archivo PDF.");
  if (file.type && file.type !== "application/pdf") {
    throw new Error("El archivo debe estar en formato PDF.");
  }
  if (file.size > 35 * 1024 * 1024) {
    throw new Error("El PDF supera el límite local de 35 MB.");
  }
  const data = new Uint8Array(await file.arrayBuffer());
  const loadingTask = getDocument({
    data,
    isEvalSupported: false,
    useSystemFonts: true,
  });
  const pdf = await loadingTask.promise;
  const pages = [];
  try {
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      onProgress?.({ pageNumber, pageCount: pdf.numPages });
      const page = await pdf.getPage(pageNumber);
      const content = await page.getTextContent();
      const lines = groupTextItems(content.items);
      const text = lines.join(" ");
      const { imageUrl, role, visuals } = await renderPagePreview(page, text);
      pages.push({
        pageNumber,
        lines,
        text,
        imageUrl,
        role,
        visuals,
      });
      page.cleanup();
    }
  } finally {
    if (typeof pdf.cleanup === "function") {
      await pdf.cleanup();
    }
    if (typeof loadingTask.destroy === "function") {
      await loadingTask.destroy();
    }
  }
  return {
    fileName: file.name,
    fileSize: file.size,
    pageCount: pages.length,
    pages,
  };
}
